// The AI report pipeline, as data. Every entry here maps to something the server actually
// reports: `key` is matched against the `stage` value from GET /api3.2/meet/recordings/:id/
// summary/status, except the terminal row, which is driven by `status === 'ready'`.
//
// A key is a WIRE VALUE, not copy. Renaming one without the matching backend change freezes the
// display on step one for every job. Adding a stage is THREE edits, in this order:
//   1. CANapps-unix src/services/canmeet/summaryJobs.ts SummaryJobStage + the runner
//   2. CANapps-unix src/controllers/canMeetController.ts:731 AND :611 - the non-terminal
//      whitelist. Skipping this makes the status endpoint answer 404 while the job runs perfectly.
//   3. this array.
// Ship the backend first: an unknown stage merely holds the display (deriveProgress rule R4),
// whereas a frontend that knows a stage the backend never emits shows a row that never activates.

export interface ReportStep {
  // Matches a server `stage`. The terminal row deliberately does not.
  key: string;
  labels: { pending: string; active: string; done: string };
  captions?: { pending?: string; active?: string; done?: string };
  // Shown when the job fails on this step. PREFERRED over the server's own error string, not a
  // last resort: the server speaks in codes a user cannot act on - browser testing caught a
  // stage-2 failure surfacing "AI_REPORT_UNAVAILABLE: OpenAI API error: 401" to the user. Because
  // the server always sends something, a server-first order would leave every fallback here
  // unreachable. A step that declares none still falls through to the server string.
  //
  // ⚠ A fallback that names a CAUSE is only safe on a step that can also claim its own failures -
  // see failureMarkers. Without that, this sentence is attached to whichever step the client last
  // watched, which is not necessarily the step that broke.
  failureFallback?: string;
  // Substrings of the server's failure message that mean THIS step is the one that broke.
  //
  // The status endpoint sends no stage on failure - only `status: 'failed'` and a message - so
  // deriveProgress otherwise blames the last step the client observed. That is wrong whenever a
  // later step fails faster than one poll interval: a revoked OpenAI key fails the report stage in
  // ~11 s against a 10 s poll, so the client never sees 'summarizing' and paints row 1 red. These
  // markers are matched against the message so the failure lands on the step that owns it.
  //
  // They are matched with `includes`, so a prefix is enough. Their source of truth is
  // CANapps-unix src/services/canmeet/summaryJobRunner.ts - the fail() calls - plus the
  // AI_REPORT_UNAVAILABLE prefix that openaiService.ts:276 deliberately keeps stable for log
  // greps. Changing a message there without changing it here silently restores the old
  // misattribution; it does not break anything louder.
  failureMarkers?: string[];
  // Offered on the row only once the step is done, so it cannot promise something that does not
  // exist yet. `id` is a dispatch key: handleProgressAction in MeetingHistory.tsx needs a matching
  // branch, or the button renders and the click only logs a warning. `requiresAdmin` HIDES the
  // control rather than disabling it - deliberate, because a disabled control would owe a tooltip
  // (docs/UI_UX_STANDARDS.md).
  action?: { id: string; label: string; requiresAdmin?: boolean };
}

// 'detached' is the client-ceiling case - the SUMMARY_POLL_CEILING_MS branch in
// MeetingHistory.tsx: the poll stopped but the job is probably still alive. It is NOT a failure
// and must never render red.
export type ReportPhase = "running" | "done" | "failed" | "detached";

// Copy rule: no third-party product name appears in any string a client can read. The
// transcription provider is never named. Anything the backend does with AI is CANai.
// "Translating audio into text" was rejected - translation means moving between languages, and
// this step produces text in the language the meeting was held in.
export const REPORT_STEPS: ReportStep[] = [
  {
    key: "transcribing",
    labels: {
      pending: "Turn audio into text",
      active: "Turning audio into text",
      done: "Transcript ready",
    },
    captions: {
      pending: "Waiting to start",
      active: "Runs only once per meeting",
    },
    failureFallback:
      "We could not turn this recording into text. This usually means the recording has no audio.",
    // Reachable the moment `stage` flips to 'summarizing' - that flip IS the signal that
    // transcription finished. downloadTranscript reaches the provider independently of the
    // summary, so this costs no backend change.
    action: { id: "transcript", label: "Download transcript", requiresAdmin: true },
  },
  {
    key: "summarizing",
    labels: {
      pending: "Write the report",
      active: "CANai is writing the report",
      done: "Report written",
    },
    captions: {
      pending: "Starts once the transcript is ready",
      active: "Pulling out decisions, action items, and key points",
    },
    failureFallback:
      "Unable to write the report. Please contact the support team. Your transcript is safe and downloadable above.",
    // Every fail() the report stage can reach: summaryJobRunner.ts:173 sends the OpenAI service's
    // own error, which openaiService.ts:282 prefixes, or its literal fallback; :182 is the save.
    failureMarkers: [
      "AI_REPORT_UNAVAILABLE",
      "Failed to generate report",
      "Failed to save the summary",
    ],
  },
  {
    // The terminal row. `ready` never arrives in `stage` - it arrives in `status` - so this key
    // matches nothing on the wire by design. Its `active` label is therefore unreachable today;
    // it exists so this row obeys the same three-label contract as every other row.
    key: "ready",
    labels: {
      pending: "Report ready",
      active: "Opening the report",
      done: "Report ready",
    },
    captions: {
      pending: "Opens automatically",
      done: "Opening now",
    },
  },
];

export const REPORT_PANEL_HEADINGS: Record<ReportPhase, string> = {
  running: "Preparing your AI report",
  done: "Your report is ready",
  failed: "This report did not finish",
  // The job is still running in Node; we just stopped watching it. The heading must not imply
  // otherwise.
  detached: "Preparing your AI report",
};

// Shown while running, hidden in every terminal state.
export const REPORT_RUNNING_REASSURANCE =
  "You can close this window — the report will be waiting when you come back.";
